import React, { useEffect, useMemo, useState } from 'react';
/**
 * FillInRenderer — Text/number input for open-ended questions.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function FillInRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isFinePointer, setIsFinePointer] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      setIsFinePointer(window.matchMedia('(pointer: fine)').matches);
    }
  }, []);
  const config = question.data || {};
  const rawPlaceholder = config.placeholder || 'Typ je antwoord...';
  const inputType = config.inputType || 'text';
  // Determine if it's a currency/numeric question
  const hasEuroPlaceholder = rawPlaceholder.includes('€');
  // R7-DID1: Auto-detect numeric mode when all acceptedAnswers are numeric
  const hasNumericAnswers = (() => {
    const accepted: string[] = config.acceptedAnswers || [];
    if (accepted.length === 0) return false;
    return accepted.every((a: string) => {
      const normalized = String(a).replace(/[€\s,]/g, '.').trim();
      return normalized !== '' && !isNaN(parseFloat(normalized));
    });
  })();
  const isNumeric = inputType === 'number' || config.unit === '€' || hasEuroPlaceholder || hasNumericAnswers;
  // Determine unit and position
  let displayUnit = config.unit || '';
  let isPrefix = false;
  if (hasEuroPlaceholder && !displayUnit) {
    displayUnit = '€';
  }
  if (displayUnit === '€' || displayUnit === '$' || displayUnit === '£') {
    isPrefix = true;
  }
  // Clean up placeholder
  const placeholder = hasEuroPlaceholder && rawPlaceholder.length <= 4 ? '0,00' : rawPlaceholder.replace('€', '').trim();
  useEffect(() => {
    setValue('');
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(value.trim().length > 0);
  }, [value, onReadyChange]);
  // Edge-triggered submit (R-03): compute payload once, hook handles rising edge.
  const answerPayload = useMemo(() => inputType === 'number' ? Number(value) : value, [inputType, value]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (isNumeric) {
      // Allow only digits, comma, period, and minus sign
      val = val.replace(/[^0-9.,-]/g, '');
    }
    setValue(val);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-md mx-auto" data-id="element-11623" data-mp="3n7k7edgqbk18">
      <motion.div animate={{
      scale: isFocused ? 1.02 : 1
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 20
    }} className="flex items-stretch bg-white border-2 rounded-2xl overflow-hidden transition-all shadow-sm" style={isFocused ? {
      borderColor: color,
      '--tw-ring-color': hexToRgba(color, 0.15),
      '--tw-ring-offset-width': '0px',
      '--tw-ring-offset-color': '#fff',
      '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
      '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
      boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000'
    } : {
      borderColor: '#e2e8f0' // slate-200
    }} data-id="element-11624" data-mp="12l3ei6sbjwnn">
        {displayUnit && isPrefix && <div className="flex items-center justify-center pl-5 pr-3 py-5 text-xl font-bold text-slate-500 bg-slate-50 border-r border-slate-200 select-none" data-id="element-11625" data-mp="1ou8t6vqft8t5">
            <LatexRenderer content={displayUnit} data-id="element-11626" data-mp="2gkdk7d8pcu3c" />
          </div>}

        <input type="text" inputMode={isNumeric ? 'decimal' : 'text'} pattern={isNumeric ? '[0-9.,-]*' : undefined} value={value} onChange={handleChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder={placeholder} className="flex-1 p-5 text-xl font-medium text-slate-800 outline-none bg-transparent w-full"
      // INTERACTIVE-3.3: only autoFocus on devices with a fine pointer
      // (desktop/laptop). On touch devices, autoFocus triggers a sudden
      // keyboard pop-up that scrolls the question off-screen.
      autoFocus={isFinePointer} data-id="element-11627" data-mp="2gmhmxruv7n21" />

        {displayUnit && !isPrefix && <div className="flex items-center justify-center pr-5 pl-3 py-5 text-xl font-bold text-slate-500 bg-slate-50 border-l border-slate-200 select-none" data-id="element-11628" data-mp="3edednvnwwlj6">
            <LatexRenderer content={displayUnit} data-id="element-11629" data-mp="2qqem17ufklte" />
          </div>}
      </motion.div>

      <p className="text-center text-slate-400 text-sm mt-6 font-medium" data-id="element-11630" data-mp="2motcnwh3upny">
        {value.length > 0 ? 'Klaar? Klik op Controleren!' : 'Typ je antwoord in het vak hierboven'}
      </p>
    </motion.div>;
}